import { useRef, useState } from 'react'

import { PROFILE_ROUTE_NAMES } from '../../../../constants/route'
import { ScreenContainerRef } from '../../../../context'
import { useMinimizedModalsContext, useOutboundUuid } from '../../../../hooks'
import {
  EmailProfileActionsContextProvider,
  EmailProfileContextProvider,
  EmailProfileMessagesListExpandContextProvider,
} from '../../../Email'
import Modal, {
  Box,
  MINIMIZED_MODAL_KEY,
  MINIMIZED_MODAL_SCOPE,
  useMinimizableModal,
} from '../../../Modal'
import {
  getRequestThreadMessagePrintPageLink,
  getRequestThreadMessageRawPageLink,
} from '../../../Request'

import { QA_NAME_REQUEST_PROFILE_EMAIL_THREAD_MODAL } from './RequestProfileTabEmailsThreadModal.constants'
import {
  useRequestProfileTabEmailsThreadModalHeaderActions,
  useRequestProfileTabEmailsThreadModalLoaders,
  useRequestProfileTabEmailsThreadModalMessageHandlers,
  useRequestProfileTabEmailsThreadModalMessageItemProps,
  useRequestProfileTabEmailsThreadModalNavigation,
  useRequestProfileTabEmailsThreadModalSummaryProps,
} from './RequestProfileTabEmailsThreadModal.hooks'
import { RequestProfileTabEmailsThreadModalActionsHeader } from './RequestProfileTabEmailsThreadModalActionsHeader'
import { RequestProfileTabEmailsThreadModalBody } from './RequestProfileTabEmailsThreadModalBody'
import { RequestProfileTabEmailsThreadModalHeader } from './RequestProfileTabEmailsThreadModalHeader'

import * as styles from './RequestProfileTabEmailsThreadModal.module.css'

function RequestProfileTabEmailsThreadModal({
  mailUuid: initialMailUuid,
  navigationContext,
  requestId,
  onClose,
  onReload,
  onSuccessRead,
}) {
  const outboundUuid = useOutboundUuid()

  const { confirmBeforeAction } = useMinimizedModalsContext()

  const [modal, setModal] = useState(null)
  const [isPerformingMessageAction, setPerformingMessageAction] =
    useState(false)

  const scrollWrapperRef = useRef(null)

  const {
    currentIndex,
    currentUuid: mailUuid,
    handleCurrentThreadRemoved,
    handleNext,
    handlePrevious,
    hasNext,
    hasPrevious,
    isLoading: isNavigationLoading,
    totalCount,
  } = useRequestProfileTabEmailsThreadModalNavigation({
    initialUuid: initialMailUuid,
    navigationContext,
  })

  const {
    entityData,
    getMessageThresholdItemsProps,
    handleReloadProfile,
    handleUpdateEntity,
    isInitiated,
    isReloadingThread,
    loadEmailMessages,
    loadingMoreMessage,
    messages,
  } = useRequestProfileTabEmailsThreadModalLoaders({
    isReadEnabled: !isNavigationLoading,
    mailUuid,
    requestId,
    onSuccessRead,
  })

  const isReadyToShowMessages =
    isInitiated && messages !== null && !isReloadingThread

  const isThreadUnavailable = isReadyToShowMessages && messages.length === 0

  // Removal actions stay disabled while the neighbours are being looked up
  // (`isNavigationLoading`), so the successor is known when this runs
  function handleThreadRemoved() {
    if (!handleCurrentThreadRemoved()) {
      onClose?.()
    }
  }

  const { handleClickAction, iconBarActions, isPerformingAction, menuActions } =
    useRequestProfileTabEmailsThreadModalHeaderActions({
      directoryId: entityData?.directory_id,
      entityData,
      mailUuid,
      messages,
      minimizedModalScope: MINIMIZED_MODAL_SCOPE.REQUEST_PROFILE_EMAIL_THREAD,
      requestId,
      setModal,
      onClose,
      onReloadList: onReload,
      onReloadProfile: handleReloadProfile,
      onThreadRemoved: handleThreadRemoved,
    })

  const { handleMinimize, isMinimized } = useMinimizableModal({
    minimizeKey: MINIMIZED_MODAL_KEY.REQUEST_PROFILE_EMAIL_THREAD,
    qaName: QA_NAME_REQUEST_PROFILE_EMAIL_THREAD_MODAL,
    title: entityData?.subject,
    onClose,
  })

  function handleCloseModal() {
    setModal(null)
  }

  // Guards only user-initiated closes (header X, overlay); programmatic
  // closes (thread removed, force close) keep the raw `onClose`.
  function handleGuardedClose() {
    confirmBeforeAction(() => onClose?.(), {
      scope: MINIMIZED_MODAL_SCOPE.REQUEST_PROFILE_EMAIL_THREAD,
    })
  }

  const { handleMarkAsUnread, handleSuccessReply, updateMessage } =
    useRequestProfileTabEmailsThreadModalMessageHandlers({
      mailUuid,
      onReloadList: onReload,
      onReloadProfile: handleReloadProfile,
    })

  const messageItemProps =
    useRequestProfileTabEmailsThreadModalMessageItemProps({
      audience: entityData?.audience,
      getPrintPageLink: ({ additionalParams, id }) =>
        getRequestThreadMessagePrintPageLink({
          additionalParams,
          id,
          outboundUuid,
          requestId,
        }),
      getRawPageLink: ({ additionalParams, id }) =>
        getRequestThreadMessageRawPageLink({
          additionalParams,
          id,
          outboundUuid,
          requestId,
        }),
      isActionsDisabled: isNavigationLoading,
      minimizedModalScope: MINIMIZED_MODAL_SCOPE.REQUEST_PROFILE_EMAIL_THREAD,
      requestId,
      onReloadList: onReload,
      onSuccessReply: handleSuccessReply,
      onThreadRemoved: handleThreadRemoved,
    })

  const summaryProps = useRequestProfileTabEmailsThreadModalSummaryProps({
    entityData,
    mailUuid,
    onReloadProfile: handleReloadProfile,
  })

  return (
    <Modal
      hidden={isMinimized}
      qaName={QA_NAME_REQUEST_PROFILE_EMAIL_THREAD_MODAL}
      onClose={handleGuardedClose}
    >
      <Box isFullScreen>
        <EmailProfileContextProvider
          entityData={entityData}
          handleCloseModal={handleCloseModal}
          handleReloadEntity={handleReloadProfile}
          handleReloadMessages={loadEmailMessages}
          handleUpdateEntity={handleUpdateEntity}
          handleUpdateMessage={updateMessage}
          isInitiated={isReadyToShowMessages}
          mailUuid={mailUuid}
          messages={messages}
          setModal={setModal}
          setPerformingAction={setPerformingMessageAction}
          sub={null}
        >
          <EmailProfileActionsContextProvider
            getPrintPageLink={PROFILE_ROUTE_NAMES.emailThreadPrint}
            onUnread={handleMarkAsUnread}
          >
            <EmailProfileMessagesListExpandContextProvider messages={messages}>
              <ScreenContainerRef value={scrollWrapperRef}>
                <div className={styles.RequestProfileTabEmailsThreadModal}>
                  <RequestProfileTabEmailsThreadModalHeader
                    isSkeleton={!isReadyToShowMessages}
                    mailUuid={mailUuid}
                    requestId={requestId}
                    subject={entityData?.subject}
                    onClose={handleGuardedClose}
                    onMinimize={handleMinimize}
                  />
                  <RequestProfileTabEmailsThreadModalActionsHeader
                    actionsDisabled={
                      isThreadUnavailable ||
                      isPerformingAction ||
                      isPerformingMessageAction ||
                      isNavigationLoading
                    }
                    currentIndex={currentIndex}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    iconBarActions={iconBarActions}
                    isNavigationDisabled={
                      isNavigationLoading ||
                      isPerformingAction ||
                      isPerformingMessageAction
                    }
                    isReadyToShowMessage={isReadyToShowMessages}
                    menuActions={menuActions}
                    totalCount={totalCount}
                    onClickAction={handleClickAction}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                  />
                  <RequestProfileTabEmailsThreadModalBody
                    containerRef={scrollWrapperRef}
                    getMessageThresholdItemsProps={
                      getMessageThresholdItemsProps
                    }
                    isInitiated={isInitiated}
                    isReloadingThread={isReloadingThread}
                    itemProps={messageItemProps}
                    loadingMore={loadingMoreMessage}
                    mailUuid={mailUuid}
                    messages={messages}
                    summaryProps={summaryProps}
                  />
                </div>
                {modal}
              </ScreenContainerRef>
            </EmailProfileMessagesListExpandContextProvider>
          </EmailProfileActionsContextProvider>
        </EmailProfileContextProvider>
      </Box>
    </Modal>
  )
}

export default RequestProfileTabEmailsThreadModal
